import React, { useEffect, useMemo, useState } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

type Item = {
  id: string;
  text: string;
};

type Props = {
  items: Item[];
  className?: string;
};

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function HeroChecklist({ items, className }: Props) {
  const reduced = usePrefersReducedMotion();
  const safe = useMemo(() => items.filter(Boolean), [items]);

  const STEP_MS = 1400;
  const CHECK_DELAY_MS = 500;

  const [visibleCount, setVisibleCount] = useState<number>(
    reduced ? safe.length : 0
  );
  const [checkedCount, setCheckedCount] = useState<number>(
    reduced ? safe.length : 0
  );

  useEffect(() => {
    if (reduced) {
      setVisibleCount(safe.length);
      setCheckedCount(safe.length);
      return;
    }

    setVisibleCount(0);
    setCheckedCount(0);

    if (safe.length === 0) return;

    let i = 0;

    const interval = window.setInterval(() => {
      if (i >= safe.length) {
        window.clearInterval(interval);
        return;
      }

      setVisibleCount((c) => Math.min(c + 1, safe.length));

      const currentIndex = i;
      window.setTimeout(() => {
        setCheckedCount((c) =>
          Math.min(currentIndex + 1, safe.length)
        );
      }, CHECK_DELAY_MS);

      i++;
    }, STEP_MS);

    return () => window.clearInterval(interval);
  }, [reduced, safe.length]);

  return (
    <div
      className={`zg-hero-checklist ${className ?? ""}`}
      aria-label="Kurzüberblick"
    >
      {safe.map((it, idx) => {
        const isVisible = idx < visibleCount;
        const isChecked = idx < checkedCount;

        return (
          <div
            key={it.id}
            className={`zg-hero-checkrow ${
              isVisible ? "is-visible" : ""
            } ${isChecked ? "is-checked" : ""}`}
          >
            <span className="zg-hero-check" aria-hidden="true">
              <CheckIcon />
            </span>
            <span className="zg-hero-checktext">{it.text}</span>
          </div>
        );
      })}
    </div>
  );
}